<template>
  <div class="file-explorer-container">
    <!-- Cabeçalho -->
    <div class="file-explorer-header">
      <div class="title">
        <feather-icon icon="HardDriveIcon" size="18" class="title-icon" />
        {{ title }}
      </div>
      <div class="toolbar">
        <button
          v-permission="'drive.upload'"
          @click="$emit('show-upload-dialog')"
          class="toolbar-button"
          :class="{ 'disabled-button': isRootLevel }"
          :disabled="isRootLevel || isSharedContext"
          title="Upload de arquivo"
        >
          <feather-icon icon="UploadIcon" size="16" />
        </button>

        <button
          v-permission="'drive.create_directory'"
          @click="$emit('show-new-folder-dialog')"
          class="toolbar-button"
          :class="{ 'disabled-button': isRootLevel }"
          :disabled="isRootLevel || isSharedContext"
          title="Nova pasta"
        >
          <feather-icon icon="FolderPlusIcon" size="16" />
        </button>

        <div class="view-toggle">
          <button
            @click="viewMode = 'grid'"
            :class="['toolbar-button', { active: viewMode === 'grid' }]"
            title="Visualização em grade"
          >
            <feather-icon icon="GridIcon" size="16" />
          </button>

          <button
            @click="viewMode = 'list'"
            :class="['toolbar-button', { active: viewMode === 'list' }]"
            title="Visualização em lista"
          >
            <feather-icon icon="ListIcon" size="16" />
          </button>
        </div>
      </div>
    </div>

    <!-- Barra de navegação com breadcrumb -->
    <div class="breadcrumb-container">
      <div class="breadcrumb">
        <div class="breadcrumb-nav-buttons">
          <button
            @click="toggleTreeView"
            class="toolbar-button"
            :class="{ active: treeViewVisible }"
            title="Visualizar estrutura em árvore"
          >
            <feather-icon icon="AlignJustifyIcon" size="14" />
          </button>

          <button
            @click="navigateUp"
            :disabled="currentPath === ''"
            class="toolbar-button"
            title="Subir um nível"
          >
            <feather-icon icon="ArrowUpIcon" size="14" />
          </button>

          <button
            @click="refresh"
            class="toolbar-button"
            title="Atualizar"
          >
            <feather-icon icon="RefreshCwIcon" size="14" />
          </button>
        </div>
        <!-- Início do Drive (raiz) -->
        <span
          class="breadcrumb-item"
          @click="navigateToRoot"
          :class="{ 'active-breadcrumb': currentPath === '' }"
        >
          <feather-icon icon="HomeIcon" size="14" />
          <span class="root-label">Home</span>
        </span>

        <!-- Itens do breadcrumb dinâmicos -->
        <template v-if="breadcrumbItems.length > 0">
          <span class="breadcrumb-separator">/</span>
          <span
            v-for="(item, index) in breadcrumbItems"
            :key="`breadcrumb-${index}`"
            class="breadcrumb-part"
          >
            <span
              class="breadcrumb-item"
              @click="navigateToBreadcrumb(index)"
              :class="{ 'active-breadcrumb': index === breadcrumbItems.length - 1 }"
            >
              {{ item.name }}
            </span>
            <span v-if="index < breadcrumbItems.length - 1" class="breadcrumb-separator">/</span>
          </span>
        </template>
      </div>
    </div>

    <!-- Área de conteúdo principal com área para árvore e arquivos -->
    <div class="file-explorer-main">
      <!-- Container principal com layout de duas colunas -->
      <div class="file-explorer-body">
        <!-- Visualização em árvore (lado esquerdo) com transição suave -->
        <div class="tree-view" :class="{'visible': treeViewVisible}">
          <div class="tree-section">
            <div class="tree-section-title">
              <feather-icon icon="HardDriveIcon" size="16" style="margin-right: 6px; vertical-align: middle;" />
              Pastas
            </div>
            <file-tree
              v-show="treeViewVisible"
              :root-path="rootPath"
              :file-explorer-service="fileExplorerService"
              :drive-public-service="drivePublicService"
              :current-path="currentPath"
              @navigate="item => handleTreeNavigation(item, false)"
              ref="fileTree"
            ></file-tree>
          </div>
          <div class="tree-shared-divider" v-if="showSharedSection"></div>
          <div v-if="showSharedSection" class="tree-section">
            <div class="tree-section-title">
              <feather-icon icon="UsersIcon" size="16" style="margin-right: 6px; vertical-align: middle;" />
              Compartilhados comigo
            </div>
            <file-tree
              v-show="treeViewVisible"
              :is-shared-context="true"
              :file-explorer-service="fileExplorerService"
              :drive-public-service="drivePublicService"
              :current-path="currentPath"
              @navigate="item => handleTreeNavigation(item, true)"
              ref="fileTreeShared"
            ></file-tree>
          </div>
        </div>

        <!-- Divisor vertical permanente -->
        <div
          class="tree-view-divider"
          :class="{'visible': treeViewVisible, 'dragging': isDragging}"
          @mousedown="startResize"
          ref="divider"
        ></div>

        <!-- Área de conteúdo principal (lado direito) -->
        <div class="file-content-area">
          <div class="file-explorer-content">
            <div>
              <h4 class="section-title">{{ sectionTitle }}</h4>
              <div v-if="loading" class="loading-indicator">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
              </div>
              <div v-else-if="error" class="error-container">
                <feather-icon icon="AlertCircleIcon" size="24" />
                <div class="error-message">{{ error }}</div>
              </div>
              <div v-else @contextmenu.prevent="showEmptyAreaContextMenu($event)">
                <!-- Visualização em grade -->
                <div v-if="viewMode === 'grid'" class="file-list-grid">
                  <div
                    v-for="item in currentItems"
                    :key="getItemUniqueId(item)"
                    class="file-item"
                    :class="{
                      'selected': isItemSelected(item),
                      'drive-item': item.type === 'drive'
                    }"
                    @click="handleItemClick(item)"
                    @dblclick="handleItemDoubleClick(item)"
                    @contextmenu.prevent="showContextMenu($event, item)"
                    :title="item.name"
                  >
                    <div class="file-icon">
                      <div v-if="item.type === 'directory'" class="file-icon-wrapper">
                        <div class="file-type-icon file-folder">
                          <feather-icon
                            icon="FolderIcon"
                            size="28"
                            :class="['file-type-feather-icon', { 'folder-open': isItemSelected(item) }]"
                          />
                        </div>
                      </div>
                      <div v-else-if="item.type === 'drive'" class="file-icon-wrapper">
                        <div class="file-type-icon file-drive">
                          <feather-icon
                            icon="HardDriveIcon"
                            size="28"
                            :class="['file-type-feather-icon', { 'drive-open': isItemSelected(item) }]"
                          />
                        </div>
                      </div>
                      <div v-else class="file-icon-wrapper">
                        <div class="file-type-icon" :class="getFileClass(item)">
                          <feather-icon :icon="getIconForItem(item)" size="28" class="file-type-feather-icon" />
                          <span class="file-icon-extension">{{ getShortExtension(item) }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="file-name">{{ formatFileName(item.name) }}</div>
                  </div>
                  <div v-if="currentItems.length === 0" class="empty-folder">
                    <div class="empty-message">Esta pasta está vazia</div>
                  </div>
                </div>
                <!-- Visualização em lista -->
                <div v-else-if="viewMode === 'list'" class="file-list-table">
                  <table>
                    <thead>
                      <tr>
                        <th @click="sortBy('name')" class="sortable">
                          Nome
                          <span v-if="sortColumn === 'name'" class="sort-icon">
                            <feather-icon :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
                          </span>
                        </th>
                        <th @click="sortBy('type')" class="sortable">
                          Tipo
                          <span v-if="sortColumn === 'type'" class="sort-icon">
                            <feather-icon :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
                          </span>
                        </th>
                        <th @click="sortBy('size')" class="sortable">
                          Tamanho
                          <span v-if="sortColumn === 'size'" class="sort-icon">
                            <feather-icon :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
                          </span>
                        </th>
                        <th v-if="!isAccessPublic" @click="sortBy('owner_name')" class="sortable">
                          Proprietário
                          <span v-if="sortColumn === 'owner_name'" class="sort-icon">
                            <feather-icon :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in currentItems"
                        :key="getItemUniqueId(item)"
                        @click="handleItemClick(item)"
                        @dblclick="handleItemDoubleClick(item)"
                        @contextmenu.prevent="showContextMenu($event, item)"
                        class="file-row"
                        :class="{
                          'selected': isItemSelected(item),
                          'drive-row': item.type === 'drive'
                        }"
                      >
                        <td class="file-cell">
                          <div class="file-cell-content">
                            <div v-if="item.type === 'directory'" class="file-icon-small">
                              <div class="file-type-icon-small file-folder">
                                <feather-icon
                                  icon="FolderIcon"
                                  size="14"
                                  :class="['file-type-feather-icon', { 'folder-open': isItemSelected(item) }]"
                                />
                              </div>
                            </div>
                            <div v-else-if="item.type === 'drive'" class="file-icon-small">
                              <div class="file-type-icon-small file-drive">
                                <feather-icon
                                  icon="HardDriveIcon"
                                  size="14"
                                  :class="['file-type-feather-icon', { 'drive-open': isItemSelected(item) }]"
                                />
                              </div>
                            </div>
                            <div v-else class="file-icon-small">
                              <div class="file-type-icon-small" :class="getFileClass(item)">
                                <feather-icon :icon="getIconForItem(item)" size="14" class="file-type-feather-icon" />
                                <span class="file-icon-extension-small">{{ getShortExtension(item) }}</span>
                              </div>
                            </div>
                            <span class="file-cell-name" :title="item.name">{{ formatFileName(item.name) }}</span>
                          </div>
                        </td>
                        <td>
                          <div v-if="item.type === 'directory' || item.type === 'drive'" class="item-type-with-count">
                            {{ getTypeLabel(item) }}
                            <span v-if="showItemCounts(item)" class="item-count-badge">
                              {{ item.folder_count || 0 }} pastas, {{ item.file_count || 0 }} arquivos
                            </span>
                          </div>
                          <div v-else>
                            {{ formatFileType(item) }}
                          </div>
                        </td>
                        <td>{{ item.type === 'directory' || item.type === 'drive' ? '--' : formatFileSize(item.size) }}</td>
                        <td v-if="!isAccessPublic">{{ item.is_owner ? 'Eu' : (item.owner_name || '--') }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="currentItems.length === 0" class="empty-folder">
                    <div class="empty-message">Esta pasta está vazia</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer separado -->
    <div class="file-explorer-footer">
      <div class="item-count">{{ itemCountText }}</div>
    </div>

    <!-- Menus de contexto -->
    <div v-show="contextMenuVisible" class="context-menu" :style="contextMenuStyle">
      <div v-if="contextMenuItem">
        <!-- Opções específicas para drives -->
        <template v-if="contextMenuItem.type === 'drive'">
          <div
            class="context-menu-item"
            @click="handleContextMenuAction('open', $event)"
          >
            <feather-icon icon="HardDriveIcon" size="14" class="context-menu-icon" />
            <span>Abrir drive</span>
          </div>

          <div v-if="contextMenuItem.is_owner && !isSharedContext"
            v-permission="'drive.share_drive'"
            class="context-menu-item"
            @click="handleContextMenuAction('share', $event)"
          >
            <feather-icon icon="ShareIcon" size="14" class="context-menu-icon" />
            <span>Compartilhar</span>
          </div>

          <div
            v-permission="'drive.download_drive'"
            class="context-menu-item"
            @click="handleContextMenuAction('downloadZip', $event)"
          >
            <feather-icon icon="DownloadCloudIcon" size="14" class="context-menu-icon" />
            <span>Baixar como ZIP</span>
          </div>

          <div
            class="context-menu-item"
            @click="handleContextMenuAction('details', $event)"
          >
            <feather-icon icon="InfoIcon" size="14" class="context-menu-icon" />
            <span>Detalhes</span>
          </div>
          <div
            v-if="!isSharedContext"
            v-permission="'drive.sharing_info'"
            class="context-menu-item"
            @click="handleContextMenuAction('sharing-info', $event)"
          >
            <feather-icon icon="UsersIcon" size="14" class="context-menu-icon" />
            <span>Ver compartilhamentos</span>
          </div>
        </template>

        <!-- Opções específicas para pastas -->
        <template v-else-if="contextMenuItem.type === 'directory'">
          <div
            class="context-menu-item"
            @click="handleContextMenuAction('open', $event)"
          >
            <feather-icon icon="FolderIcon" size="14" class="context-menu-icon" />
            <span>Abrir pasta</span>
          </div>

          <div v-if="contextMenuItem.is_owner && !isSharedContext"
            v-permission="'drive.share_directory'"
            class="context-menu-item"
            @click="handleContextMenuAction('share', $event)"
          >
            <feather-icon icon="ShareIcon" size="14" class="context-menu-icon" />
            <span>Compartilhar</span>
          </div>

          <div
            v-permission="'drive.download_directory'"
            class="context-menu-item"
            @click="handleContextMenuAction('downloadZip', $event)"
          >
            <feather-icon icon="DownloadCloudIcon" size="14" class="context-menu-icon" />
            <span>Baixar como ZIP</span>
          </div>

          <div v-if="contextMenuItem.is_owner && !isSharedContext"
            v-permission="'drive.rename_directory'"
            class="context-menu-item"
            @click="handleContextMenuAction('rename', $event)"
          >
            <feather-icon icon="EditIcon" size="14" class="context-menu-icon" />
            <span>Renomear</span>
          </div>
          <div v-if="contextMenuItem.is_owner && !isSharedContext"
            v-permission="'drive.delete_directory'"
            class="context-menu-item"
            @click="handleContextMenuAction('delete', $event)"
          >
            <feather-icon icon="Trash2Icon" size="14" class="context-menu-icon" />
            <span>Excluir</span>
          </div>
          <div
            class="context-menu-item"
            @click="handleContextMenuAction('details', $event)"
          >
            <feather-icon icon="InfoIcon" size="14" class="context-menu-icon" />
            <span>Detalhes</span>
          </div>
          <div
            v-if="!isSharedContext"
            v-permission="'drive.sharing_info'"
            class="context-menu-item"
            @click="handleContextMenuAction('sharing-info', $event)"
          >
            <feather-icon icon="UsersIcon" size="14" class="context-menu-icon" />
            <span>Ver compartilhamentos</span>
          </div>
        </template>

        <!-- Opções específicas para arquivos -->
        <template v-else>
          <!--
          <div
            class="context-menu-item"
            @click="handleContextMenuAction('preview', $event)"
          >
            <feather-icon icon="EyeIcon" size="14" class="context-menu-icon" />
            <span>Visualizar</span>
          </div>
        -->

          <div
            class="context-menu-item"
            @click="handleContextMenuAction('download', $event)"
          >
            <feather-icon icon="DownloadIcon" size="14" class="context-menu-icon" />
            <span>Download</span>
          </div>

          <div v-if="contextMenuItem.is_owner && !isSharedContext"
            v-permission="'drive.share_file'"
            class="context-menu-item"
            @click="handleContextMenuAction('share', $event)"
          >
            <feather-icon icon="ShareIcon" size="14" class="context-menu-icon" />
            <span>Compartilhar</span>
          </div>

          <div v-if="contextMenuItem.is_owner && !isSharedContext"
            v-permission="'drive.rename_file'"
            class="context-menu-item"
            @click="handleContextMenuAction('rename', $event)"
          >
            <feather-icon icon="EditIcon" size="14" class="context-menu-icon" />
            <span>Renomear</span>
          </div>

          <div v-if="contextMenuItem.is_owner && !isSharedContext"
            v-permission="'drive.delete_file'"
            class="context-menu-item"
            @click="handleContextMenuAction('delete', $event)"
          >
            <feather-icon icon="Trash2Icon" size="14" class="context-menu-icon" />
            <span>Excluir</span>
          </div>

          <div
            class="context-menu-item"
            @click="handleContextMenuAction('details', $event)"
          >
            <feather-icon icon="InfoIcon" size="14" class="context-menu-icon" />
            <span>Detalhes</span>
          </div>
          <div
            v-if="!isSharedContext"
            v-permission="'drive.sharing_info'"
            class="context-menu-item"
            @click="handleContextMenuAction('sharing-info', $event)"
          >
            <feather-icon icon="UsersIcon" size="14" class="context-menu-icon" />
            <span>Ver compartilhamentos</span>
          </div>
        </template>
      </div>
    </div>

    <div v-show="emptyAreaContextMenuVisible" class="context-menu" :style="emptyAreaContextMenuStyle">
      <div v-if="!isSharedContext"
        v-permission="'drive.upload'"
        class="context-menu-item"
        @click="isRootLevel ? null : handleEmptyAreaContextMenuAction('upload', $event)"
        :class="{ 'disabled': isRootLevel }"
      >
        <feather-icon icon="UploadIcon" size="14" class="context-menu-icon" />
        <span>Upload de arquivo</span>
      </div>

      <div v-if="!isSharedContext"
        v-permission="'drive.create_directory'"
        class="context-menu-item"
        @click="isRootLevel ? null : handleEmptyAreaContextMenuAction('newFolder', $event)"
        :class="{ 'disabled': isRootLevel }"
      >
        <feather-icon icon="FolderPlusIcon" size="14" class="context-menu-icon" />
        <span>Nova pasta</span>
      </div>

      <div
        class="context-menu-item"
        @click="handleEmptyAreaContextMenuAction('refresh', $event)"
      >
        <feather-icon icon="RefreshCwIcon" size="14" class="context-menu-icon" />
        <span>Atualizar</span>
      </div>
    </div>

    <!-- Modal de renomeação -->
    <div class="rename-modal-backdrop" v-if="showRenameModal" @click="cancelRename">
      <div class="rename-modal" @click.stop>
        <div class="rename-modal-header">
          <h3>
            Renomear {{ renameItem && renameItem.type === 'directory' ? 'Pasta' : 'Arquivo' }}
          </h3>
          <button class="close-button" @click="cancelRename">
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>
        <div class="rename-modal-body">
          <div class="form-group">
            <label for="newName">Novo nome:</label>
            <input
              type="text"
              id="newName"
              v-model="newItemName"
              class="form-control"
              @keyup.enter="confirmRename"
              ref="renameInput"
            />
          </div>
        </div>
        <div class="rename-modal-footer">
          <button class="btn btn-light" @click="cancelRename">Cancelar</button>
          <button
            class="btn btn-primary"
            :disabled="!newItemName || isRenaming"
            @click="confirmRename"
          >
            <div v-if="isRenaming" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Renomeando...</span>
            </div>
            <span v-else>Renomear</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Adicionar o modal de preview -->
    <file-preview-modal
      v-if="showPreviewModal"
      :show="showPreviewModal"
      :file="previewFile"
      :preview-url="previewUrl"
      @close="closePreview"
      @download="handlePreviewDownload"
    />
  </div>
</template>

<script>
import DriveService from '@/services/api/DriveService';
import '@/assets/scss/FileExplorer.scss';
import FileTree from './FileTree.vue';
import FilePreviewModal from './FilePreviewModal.vue';

export default {
  name: 'FileExplorerComponent',
  components: {
    FileTree,
    FilePreviewModal
  },
  props: {
    title: {
      type: String,
      default: 'Drive'
    },
    rootPath: {
      type: String,
      default: ''
    },
    drivePublicService: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      currentPath: '',
      currentResourceId: null,
      items: [],
      loading: false,
      error: null,
      selected: null,
      viewMode: 'grid', // 'grid' ou 'list'
      sortColumn: 'name',
      sortDirection: 'asc',
      isRootSelected: false,

      // Menu de contexto
      contextMenuVisible: false,
      contextMenuPosition: { x: 0, y: 0 },
      contextMenuItem: null,
      selectedItem: null,
      contextMenuX: 0,
      contextMenuY: 0,

      // Menu de contexto para área vazia
      emptyAreaContextMenuVisible: false,
      emptyAreaContextMenuPosition: { x: 0, y: 0 },

      // Visualização em árvore
      treeViewVisible: true,

      // Redimensionamento da tree-view
      isDragging: false,
      treeViewWidth: 280, // Largura inicial
      initialX: 0, // Posição inicial do mouse no eixo X
      initialWidth: 280, // Largura inicial da tree-view antes do arrasto

      // Serviço
      fileExplorerService: null,

      // Para renomeação
      showRenameModal: false,
      renameItem: null,
      newItemName: '',
      isRenaming: false,
      breadcrumbItems: [],
      showPreviewModal: false,
      previewFile: null,
      previewUrl: null,
      // NOVO: Itens compartilhados
      sharedItems: [],
      loadingShared: false,
      errorShared: null,
      // NOVO: Estado para saber se está navegando em compartilhados
      isBrowsingShared: false,
      sharedCurrentPath: '',
      sharedCurrentResourceId: null,
      sharedCurrentItems: [],
      isSharedContext: false,
      currentItems: [], // lista de itens da pasta atual (meus ou compartilhados)
    }
  },
  computed: {
    displayPath() {
      if (!this.currentPath) return this.rootPath || '/';
      return `${this.rootPath || '/'}${this.currentPath}`;
    },
    itemCountText() {
      const count = this.items.length;
      return `${count} ${count === 1 ? 'item' : 'items'}`;
    },
    pathParts() {
      if (!this.currentPath) return [];
      return this.currentPath.split('/').filter(part => part);
    },
    sortedItems() {
      const sorted = [...this.items];

      // Sempre coloca diretórios primeiro, independente da coluna de ordenação
      const compareFn = (a, b) => {
        // Diretórios sempre primeiro
        if (a.type === 'directory' && b.type !== 'directory') return -1;
        if (a.type !== 'directory' && b.type === 'directory') return 1;

        // Ordena pelo campo escolhido
        let valA, valB;

        switch (this.sortColumn) {
          case 'size':
            valA = a.size || 0;
            valB = b.size || 0;
            break;
          case 'modified':
            valA = a.modified ? new Date(a.modified).getTime() : 0;
            valB = b.modified ? new Date(b.modified).getTime() : 0;
            break;
          case 'type':
            valA = a.type === 'directory' ? '' : (a.extension || '');
            valB = b.type === 'directory' ? '' : (b.extension || '');
            break;
          case 'owner_name':
            valA = (a.owner_name || '').toLowerCase();
            valB = (b.owner_name || '').toLowerCase();
            break;
          case 'name':
          default:
            valA = a.name.toLowerCase();
            valB = b.name.toLowerCase();
            break;
        }

        // Aplicar a direção da ordenação
        if (this.sortDirection === 'asc') {
          return valA > valB ? 1 : -1;
        } else {
          return valA < valB ? 1 : -1;
        }
      };

      return sorted.sort(compareFn);
    },
    contextMenuStyle() {
      return {
        top: `${this.contextMenuPosition.y}px`,
        left: `${this.contextMenuPosition.x}px`
      };
    },
    emptyAreaContextMenuStyle() {
      return {
        top: `${this.emptyAreaContextMenuPosition.y}px`,
        left: `${this.emptyAreaContextMenuPosition.x}px`
      };
    },
    /**
     * Verifica se estamos no nível raiz (listando drives)
     * Neste nível, alguns botões devem ficar desabilitados
     */
    isRootLevel() {
      // Estamos no nível raiz se não houver caminho atual definido
      // e não temos um recurso específico selecionado
      return this.currentPath === '' || this.currentPath === '/' ||
        (this.currentResourceId === null && this.pathParts.length <= 0);
    },
    isAccessPublic() {
      return this.drivePublicService?.isPublic ?? false;
    },
    showSharedSection() {
      return !this.isAccessPublic
    },
    sectionTitle() {
      return this.isSharedContext ? 'Compartilhados comigo' : 'Meus Arquivos';
    }
  },
  created() {
    // Inicializar o serviço
    this.fileExplorerService = DriveService.build(this.$vs);
  },
  mounted() {
    this.loadItems();
    // NOVO: carregar compartilhados
    //this.loadSharedItems();

    // Fechar o menu de contexto ao clicar fora dele
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeyDown);

    // Escutar evento de item excluído
    this.$on('item-deleted', this.handleItemDeleted);

    // Configurar inicialização do componente
    this.init();

    // Adicionar event listeners para o redimensionamento
    document.addEventListener('mousemove', this.handleResize);
    document.addEventListener('mouseup', this.stopResize);
  },
  beforeDestroy() {
    // Limpar os event listeners
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeyDown);

    // Remover o listener do evento
    this.$off('item-deleted', this.handleItemDeleted);

    // Remover event listeners quando o componente for destruído
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);

    // Remover os listeners adicionais
    window.removeEventListener('resize', this.handleWindowResize);

    // Garantir que a classe resizing seja removida do body
    document.body.classList.remove('resizing');
  },
  methods: {
    /**
     * Inicializa o componente e configura handlers para eventos globais
     */
    init() {
      // Inicialização do componente
      this.treeViewWidth = 280; // Garantir que a largura inicial seja definida

      // Capturar cliques fora do componente para limpar estados
      document.addEventListener('click', (event) => {
        // Verificar se o clique foi fora do divisor
        if (this.isDragging && event.target !== this.$refs.divider) {
          this.stopResize();
        }
      });

      // Adicionar handler para atualizar estilos quando a janela for redimensionada
      window.addEventListener('resize', this.handleWindowResize);
    },

    /**
     * Manipulador para o evento de redimensionamento da janela
     */
    handleWindowResize() {
      if (this.treeViewVisible) {
        // Ajustar posições se necessário
        this.$nextTick(() => {
          const treeView = document.querySelector('.tree-view');
          const divider = this.$refs.divider;

          // Garantir que o divisor e a tree-view estejam alinhados
          if (treeView && divider && this.treeViewWidth) {
            divider.style.left = `${this.treeViewWidth}px`;
          }
        });
      }
    },
    /**
     * Carrega os itens do diretório atual
     */
    async loadItems() {
      this.loading = true;
      this.error = null;
      try {
        if (this.isAccessPublic) {
          const response = await this.drivePublicService.getResources(this.currentPath, this.currentResourceId);
          this.items = Array.isArray(response.items) ? response.items : [response.items];
        } else {
          const response = await this.fileExplorerService.listItems(this.currentPath, this.currentResourceId);
          this.items = response.items;
        }
        this.currentItems = this.items;
      } catch (err) {
        console.error('Error loading files:', err);
        this.error = 'Falha ao carregar o(s) arquivo(s)';
      } finally {
        this.loading = false;
      }
    },
    /**
     * Carrega os itens compartilhados comigo (mock por enquanto)
     */
    async loadSharedItems() {
      this.loading = true;
      this.loadingShared = true;
      this.errorShared = null;
      try {

        const response = await this.fileExplorerService.listItems(this.currentPath, this.currentResourceId, true);
        this.items = response.items;
        this.currentItems = this.items;

      } catch (err) {
        this.errorShared = 'Falha ao carregar arquivos compartilhados';
      } finally {
        this.loadingShared = false;
        this.loading = false;
      }
    },
    getIconForItem(item) {
      if (item.type === 'directory') {
        return 'FolderIcon';
      }

      if (item.type === 'drive') {
        return 'HardDriveIcon';
      }

      // Usar ícones adequados baseados no tipo de arquivo
      const extension = item.extension || this.getFileExtension(item.name);

      switch (extension.toLowerCase()) {
        case 'pdf':
          return 'FileTextIcon';
        case 'txt':
          return 'FileTextIcon';
        case 'doc':
        case 'docx':
          return 'FileTextIcon';
        case 'xls':
        case 'xlsx':
        case 'csv':
          return 'FileTextIcon';
        case 'ppt':
        case 'pptx':
          return 'FileTextIcon';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'svg':
        case 'webp':
          return 'ImageIcon';
        case 'mp3':
        case 'wav':
        case 'ogg':
        case 'flac':
          return 'MusicIcon';
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'mkv':
        case 'webm':
          return 'VideoIcon';
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
          return 'PackageIcon';
        case 'html':
        case 'css':
        case 'js':
        case 'ts':
          return 'CodeIcon';
        case 'xml':
        case 'json':
          return 'FileIcon';
        default:
          return 'FileIcon';
      }
    },
    getFileExtension(filename) {
      return filename.split('.').pop()?.toLowerCase() || '';
    },
    getFormattedFileExtension(item) {
      if (item.type === 'directory') return '';

      // Usar a extensão do item se disponível, ou extrair do nome
      const extension = item.extension || this.getFileExtension(item.name);

      // Limitar o tamanho da extensão para exibição
      return extension.length > 4 ? extension.substring(0, 3) + '…' : extension;
    },
    formatFileName(name) {
      // Para nomes muito longos, adicionar espaços onde possível para permitir quebra de linha
      if (name.length > 15 && !name.includes(' ')) {
        // Adicionar quebras a cada 8 caracteres para nomes muito longos sem espaços
        let formattedName = '';
        for (let i = 0; i < name.length; i++) {
          formattedName += name[i];
          if (i > 0 && i < name.length - 1 && i % 8 === 0) {
            formattedName += '\u200B'; // Adicionar quebra de linha invisível
          }
        }
        return formattedName;
      }
      return name;
    },
    getFileClass(item) {
      if (item.type === 'directory') return 'file-folder';
      if (item.type === 'drive') return 'file-drive';

      const extension = item.extension || this.getFileExtension(item.name);

      switch (extension.toLowerCase()) {
        case 'pdf':
          return 'file-pdf';
        case 'txt':
          return 'file-txt';
        case 'doc':
        case 'docx':
          return 'file-doc';
        case 'xls':
        case 'xlsx':
        case 'csv':
          return 'file-xls';
        case 'ppt':
        case 'pptx':
          return 'file-ppt';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'svg':
        case 'webp':
          return 'file-img';
        case 'mp3':
        case 'wav':
        case 'ogg':
        case 'flac':
          return 'file-audio';
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'mkv':
        case 'webm':
          return 'file-video';
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
          return 'file-zip';
        case 'html':
          return 'file-code';
        case 'css':
          return 'file-code';
        case 'js':
          return 'file-code';
        case 'ts':
          return 'file-code';
        case 'xml':
          return 'file-data';
        case 'json':
          return 'file-data';
        default:
          return 'file-generic';
      }
    },
    updateSharedContextFromItem(item) {
      if (
        !item ||
        item.type === 'file' ||
        typeof item.is_owner !== 'boolean'
      ) {
        return;
      }
      this.isSharedContext = !item.is_owner;
    },
    handleItemClick(item) {
      this.selected = item;
      this.updateSharedContextFromItem(item);

      // Emitir evento adequado com base no tipo do item
      if (item.type === 'drive') {
        this.$emit('drive-selected', item);
        this.isRootSelected = true;
      } else if (item.type === 'directory') {
        this.$emit('item-selected', item);
        this.isRootSelected = false;
      } else {
        this.$emit('file-selected', item);
        this.isRootSelected = false;
      }
    },
    handleItemDoubleClick(item) {
      this.updateSharedContextFromItem(item);
      if (item.type === 'directory' || item.type === 'drive') {
        // Ao navegar para um novo diretório ou drive, verificar se já existe no breadcrumb
        const existingIndex = this.breadcrumbItems.findIndex(
          breadcrumbItem => breadcrumbItem.resource_id === item.resource_id
        );

        if (existingIndex >= 0) {
          // Se já existir, cortar o breadcrumb até este item
          this.breadcrumbItems = this.breadcrumbItems.slice(0, existingIndex + 1);
        } else {
          // Caso contrário, adicionar ao breadcrumb
          this.breadcrumbItems.push({
            name: item.name,
            path: item.path,
            resource_id: item.resource_id,
            parent_id: item.parent_id || null
          });
        }

        // Navegar para a pasta
        this.navigateToFolder(item.path, item.resource_id);
      } else {
        // Para arquivos, emitir evento file-open
        this.$emit('file-open', item);
      }
    },
    navigateToFolder(path, resourceId = null) {
      this.currentPath = path;
      this.currentResourceId = resourceId;
      this.isRootSelected = false;
      this.loadCurrentItems();
    },
    navigateUp() {
      if (!this.currentPath) return;

      // Remover o último item do breadcrumb se existir
      if (this.breadcrumbItems.length > 0) {
        this.breadcrumbItems.pop();

        // Se ainda temos itens no breadcrumb, navegar para o último
        if (this.breadcrumbItems.length > 0) {
          const lastItem = this.breadcrumbItems[this.breadcrumbItems.length - 1];
          this.currentPath = lastItem.path;
          this.currentResourceId = lastItem.resource_id;
        } else {
          // Se não sobrou nada no breadcrumb, voltamos para a raiz
          this.currentPath = '';
          this.currentResourceId = null;
          this.isRootSelected = true;
          this.selected = null;
        }
      } else {
        // Comportamento antigo usando divisão do caminho
        const parts = this.currentPath.split('/');
        parts.pop();
        this.currentPath = parts.join('/');

        // Ao subir um nível, não temos o resource_id do pai diretamente
        this.currentResourceId = null;

        // Se voltarmos ao nível raiz, selecionar o drive
        if (this.currentPath === '') {
          this.isRootSelected = true;
          this.selected = null;
        }
      }

      if(this.isSharedContext){
       this.loadSharedItems();
      }else{
        this.loadItems();
      }
    },
    navigateToRoot() {
      this.currentPath = '';
      this.currentResourceId = null;
      this.isRootSelected = true;
      this.selected = null;
      this.$emit('drive-selected');
      this.loadItems();
    },
    navigateToPath(index) {
      const parts = this.pathParts.slice(0, index + 1);
      this.currentPath = parts.join('/');
      this.currentResourceId = null;
      this.isRootSelected = false;
      this.loadItems();
    },
    refresh() {
      if(this.isSharedContext){
        this.loadSharedItems();
      }else{
        this.loadItems();
      }
    },
    sortBy(column) {
      // Se clicar na mesma coluna, inverte a direção
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    formatFileSize(size) {
      if (size === undefined || size === null) return '--';

      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let fileSize = size;
      let unitIndex = 0;

      while (fileSize >= 1024 && unitIndex < units.length - 1) {
        fileSize /= 1024;
        unitIndex++;
      }

      return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return '--';

      // Usando formatação pt-BR para data e hora
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatFileType(item) {
      if (item.type === 'directory') return 'Pasta';
      if (item.type === 'drive') return 'Drive';

      // Usar a propriedade extension que vem da API, ou extrair do nome como fallback
      const extension = item.extension ? item.extension.toLowerCase() :
                        (item.name ? item.name.split('.').pop().toLowerCase() : '');

      switch (extension) {
        case 'pdf':
          return 'Documento PDF';
        case 'txt':
          return 'Arquivo de texto';
        case 'doc':
        case 'docx':
          return 'Documento Word';
        case 'xls':
        case 'xlsx':
          return 'Planilha Excel';
        case 'csv':
          return 'Arquivo CSV';
        case 'ppt':
        case 'pptx':
          return 'Apresentação PowerPoint';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'svg':
        case 'webp':
          return 'Imagem';
        case 'mp3':
        case 'wav':
        case 'ogg':
        case 'flac':
          return 'Áudio';
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'mkv':
        case 'webm':
          return 'Vídeo';
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
          return 'Arquivo compactado';
        case 'html':
          return 'Página HTML';
        case 'css':
          return 'Estilo CSS';
        case 'js':
          return 'Script JavaScript';
        case 'ts':
          return 'Script TypeScript';
        case 'json':
          return 'Arquivo JSON';
        case 'xml':
          return 'Arquivo XML';
        default:
          return extension ? `Arquivo ${extension.toUpperCase()}` : 'Arquivo desconhecido';
      }
    },
    isItemSelected(item) {
      if (!this.selected) return false;
      return this.getItemUniqueId(this.selected) === this.getItemUniqueId(item);
    },
    // Menu de contexto
    showContextMenu(event, item) {
      // Prevenir que o menu de contexto padrão seja exibido
      event.preventDefault();
      event.stopPropagation();

      // Esconder o menu de contexto da área vazia se estiver visível
      this.emptyAreaContextMenuVisible = false;

      // Selecionar o item ao clicar com o botão direito
      this.selected = item;

      // Definir o item do menu de contexto e sua posição
      this.contextMenuItem = item;
      this.contextMenuPosition = {
        x: event.clientX,
        y: event.clientY
      };

      // Ajustar posição para evitar que o menu saia da tela
      this.$nextTick(() => {
        const menu = document.querySelector('.context-menu');
        if (menu) {
          const menuRect = menu.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          if (this.contextMenuPosition.x + menuRect.width > viewportWidth) {
            this.contextMenuPosition.x = viewportWidth - menuRect.width;
          }

          if (this.contextMenuPosition.y + menuRect.height > viewportHeight) {
            this.contextMenuPosition.y = viewportHeight - menuRect.height;
          }
        }
      });

      // Mostrar o menu
      this.contextMenuVisible = true;
    },

    // Menu de contexto para área vazia
    showEmptyAreaContextMenu(event) {
      // Prevenir comportamento padrão
      event.preventDefault();
      event.stopPropagation();

      // Esconder o menu de contexto de item se estiver visível
      this.contextMenuVisible = false;

      // Se estiver na raiz, não mostrar o menu de contexto
      if (this.isRootLevel) {
        // Apenas permitir o menu de contexto em pastas específicas, não na raiz
        this.emptyAreaContextMenuVisible = false;
        return;
      }

      // Configurar posição do menu
      this.emptyAreaContextMenuPosition = {
        x: event.clientX,
        y: event.clientY
      };

      // Ajustar posição para evitar que o menu saia da tela
      this.$nextTick(() => {
        const menu = document.querySelectorAll('.context-menu')[1]; // O segundo menu de contexto (área vazia)
        if (menu) {
          const menuRect = menu.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          if (this.emptyAreaContextMenuPosition.x + menuRect.width > viewportWidth) {
            this.emptyAreaContextMenuPosition.x = viewportWidth - menuRect.width;
          }

          if (this.emptyAreaContextMenuPosition.y + menuRect.height > viewportHeight) {
            this.emptyAreaContextMenuPosition.y = viewportHeight - menuRect.height;
          }
        }
      });

      // Mostrar o menu
      this.emptyAreaContextMenuVisible = true;
    },

    hideContextMenu() {
      this.contextMenuVisible = false;
      this.emptyAreaContextMenuVisible = false;
    },

    handleClickOutside(event) {
      // Somente fecha os menus se o clique for fora deles
      if (this.contextMenuVisible) {
        const contextMenu = document.querySelector('.context-menu');
        if (contextMenu && !contextMenu.contains(event.target)) {
          this.contextMenuVisible = false;
        }
      }

      if (this.emptyAreaContextMenuVisible) {
        const emptyAreaContextMenu = document.querySelectorAll('.context-menu')[1];
        if (emptyAreaContextMenu && !emptyAreaContextMenu.contains(event.target)) {
          this.emptyAreaContextMenuVisible = false;
        }
      }
    },

    handleKeyDown(event) {
      // Fechar os menus ao pressionar ESC
      if (event.key === 'Escape') {
        this.hideContextMenu();
      }
    },

    handleContextMenuAction(action, event) {
      console.log('handleContextMenuAction', action, event);
      const item = this.contextMenuItem;

      // Prevent default event behavior
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!item) return;
      if (this.isRestrictedSharedAction(action, item)) {
        this.hideContextMenu();
        return;
      }

      switch(action) {
        case 'open':
          if (item.type === 'directory' || item.type === 'drive') {
            this.navigateToFolder(item.path, item.resource_id);
          } else {
            this.$emit('file-open', item);
          }
          break;

        case 'preview':
          if (item.type === 'file') {
            this.showFilePreview(item);
          }
          break;

        case 'share':
          this.$emit('share-request', item);
          break;

        case 'details':
          this.$emit('show-item-details', item);
          break;

        case 'sharing-info':
          this.$emit('show-sharing-info', item);
          break;

        case 'delete':
          if (item.type === 'directory' || item.type === 'file') {
            // Armazenar referência ao item para atualização posterior
            const deletedItem = { ...item };

            // Criar a mensagem de confirmação baseada no tipo do item
            const itemType = item.type === 'directory' ? 'pasta' : 'arquivo';
            const message = `Tem certeza que deseja excluir ${itemType === 'pasta' ? 'a' : 'o'} ${itemType} "${item.name}"? Esta ação não pode ser desfeita, e, essa referência será removida das outras funções do sistema.`;

            // Exibir diálogo de confirmação
            this.$vs.dialog({
              type: 'confirm',
              color: 'warning',
              title: 'Atenção!',
              acceptText: 'Sim',
              cancelText: 'Não',
              text: message,
              accept: () => {
                console.log('Emitindo delete-item com o item:', {
                  item: deletedItem,
                  parent_id: deletedItem.parent_id,
                  resource_id: deletedItem.resource_id,
                  type: deletedItem.type,
                  name: deletedItem.name
                });

                this.$emit('delete-item', deletedItem);

                // Atualizar a árvore imediatamente após o evento de exclusão ser emitido
                if (deletedItem.type === 'directory') {
                  try {
                    // Obter o parent_id, que é o identificador único para atualização
                    const parentId = deletedItem.parent_id;

                    console.log('Atualizando árvore para item excluído:', {
                      nome: deletedItem.name,
                      parent_id: parentId,
                      path: deletedItem.path
                    });

                    // Usar $nextTick para garantir que o DOM foi atualizado antes de acessar o componente tree
                    this.$nextTick(() => {
                      // Verificar se o componente fileTree existe e tem o método refreshSubTree
                      const fileTree = this.$refs.fileTree;

                      if (fileTree) {
                        console.log('Componente FileTree encontrado, tentando atualizar');

                        // Verificar se o método existe antes de chamar
                        if (typeof fileTree.refreshSubTree === 'function') {
                          console.log('Chamando refreshSubTree com parent_id:', parentId);
                          // Passar apenas o parent_id, que é o identificador único e confiável
                          fileTree.refreshSubTree(parentId);
                        } else {
                          console.warn('Método refreshSubTree não encontrado no componente FileTree');
                          // Como alternativa, tentar recarregar toda a árvore
                          if (typeof fileTree.loadRootItems === 'function') {
                            console.log('Tentando recarregar toda a árvore com loadRootItems');
                            fileTree.loadRootItems();
                          }
                        }
                      } else {
                        console.warn('Referência ao componente FileTree não encontrada');
                      }
                    });
                  } catch (err) {
                    console.error('Erro ao atualizar árvore após exclusão:', err);
                  }
                }
              }
            });
          }
          break;

        case 'download':
          if (item.type === 'file') {
            this.$emit('download-file', item);
          }
          break;

        case 'downloadZip':
          if (item.type === 'directory' || item.type === 'drive') {
            // Mostrar confirmação para download de pasta, especialmente para possíveis pastas grandes
            this.$vs.dialog({
              type: 'confirm',
              color: 'primary',
              title: 'Download como ZIP',
              acceptText: 'Download',
              cancelText: 'Cancelar',
              text: `Você está prestes a baixar "${item.name}" e todo seu conteúdo como um arquivo ZIP. Deseja continuar?`,
              accept: () => {
                this.$emit('download-directory', item);
              }
            });
          }
          break;

        case 'rename':
          // Mostrar o modal de renomeação
          this.showRenameItem(item);
          break;
      }

      // Fechar o menu após a ação
      this.hideContextMenu();
    },

    handleEmptyAreaContextMenuAction(action, event) {
      // Prevent default event behavior
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (this.isSharedContext && (action === 'upload' || action === 'newFolder')) {
        this.hideContextMenu();
        return;
      }

      switch(action) {
        case 'upload':
          this.$emit('show-upload-dialog');
          break;

        case 'newFolder':
          this.$emit('show-new-folder-dialog');
          break;

        case 'refresh':
          this.refresh();
          break;
      }

      // Fechar o menu após a ação
      this.hideContextMenu();
    },
    isRestrictedSharedAction(action, item) {
      if (!item) return false;
      const restrictedActions = ['share', 'rename', 'delete'];
      return restrictedActions.includes(action) && (!item.is_owner || this.isSharedContext);
    },

    getShortExtension(item) {
      if (item.type === 'directory') return '';

      // Usar a extensão do item se disponível, ou extrair do nome
      const extension = item.extension || this.getFileExtension(item.name);

      // Converter para maiúsculas
      return extension.toUpperCase();
    },

    // Gerar um ID único para o item
    getItemUniqueId(item) {
      // Usar vuuid como identificador único se disponível
      if (item.vuuid) {
        return `id-${item.vuuid}`;
      }

      // Fallback para tipo + path se vuuid não estiver disponível
      return `${item.type}-${item.path}-${item.modified || ''}`;
    },

    showTreeView() {
      // Mostrar a visualização em árvore
      this.treeViewVisible = true;
    },

    /**
     * Navegação fluida: ao clicar em qualquer nó da treeview
     */
    handleTreeNavigation(item, isShared = false) {
      this.isSharedContext = isShared;
      console.log('Navegando:', item, 'isSharedContext:', isShared);
      this.currentPath = item.path;
      this.currentResourceId = item.resource_id;
      this.selected = null;
      this.loadCurrentItems();
    },
    /**
     * Carrega os itens da pasta atual (meus ou compartilhados)
     */
    loadCurrentItems() {
      if (this.isSharedContext) {
        this.loadSharedItems();
      } else {
        this.loadItems();
      }
    },
    toggleTreeView() {
      this.treeViewVisible = !this.treeViewVisible;

      // When toggling tree view, don't automatically select anything
      // Keep the current selection state intact

      // Resetar a largura da tree-view para o valor padrão quando fechada
      if (!this.treeViewVisible) {
        // Encerrar o redimensionamento se estiver ativo
        if (this.isDragging) {
          this.stopResize();
        }

        // Resetar tudo para o padrão no próximo tick
        this.$nextTick(() => {
          const treeView = document.querySelector('.tree-view');
          const divider = this.$refs.divider;

          if (treeView) {
            treeView.style.width = '';
            treeView.style.minWidth = '';
            treeView.style.maxWidth = '';
          }

          if (divider) {
            divider.style.left = '';
          }

          // Resetar as variáveis para os valores padrão
          this.treeViewWidth = 280;
          this.initialWidth = 280;
          this.initialX = 0;
        });
      } else {
        // Quando abrimos, garantir que a largura inicial seja aplicada
        this.$nextTick(() => {
          const treeView = document.querySelector('.tree-view');
          const divider = this.$refs.divider;

          if (treeView && this.treeViewWidth !== 280) {
            treeView.style.width = `${this.treeViewWidth}px`;
            treeView.style.minWidth = `${this.treeViewWidth}px`;
            treeView.style.maxWidth = `${this.treeViewWidth}px`;

            if (divider) {
              divider.style.left = `${this.treeViewWidth}px`;
            }
          }
        });
      }

      // Force layout recalculation
      this.$nextTick(() => {
        window.dispatchEvent(new Event('resize'));
      });
    },

    // Métodos para o redimensionamento da tree-view
    startResize(event) {
      if (!this.treeViewVisible) return;

      this.isDragging = true;
      document.body.classList.add('resizing');

      // Salvar a posição inicial do mouse e a largura inicial da tree-view
      // para calcular o offset correto durante o arrasto
      this.initialX = event.clientX;
      this.initialWidth = this.treeViewWidth;

      event.preventDefault();
    },

    handleResize(event) {
      if (!this.isDragging) return;

      // Calcular diferença em relação à posição inicial do mouse
      const diff = event.clientX - this.initialX;

      // Calcular a nova largura baseada na largura inicial + diferença
      // Limitando a largura mínima e máxima
      let newWidth = Math.max(200, Math.min(500, this.initialWidth + diff));

      // Atualizar a largura da tree-view
      this.treeViewWidth = newWidth;

      // Atualizar o estilo CSS das classes relevantes
      const treeView = document.querySelector('.tree-view');
      const divider = this.$refs.divider;
      const fileContentArea = document.querySelector('.file-content-area');

      if (treeView && divider) {
        // Aplicar a largura diretamente ao estilo do elemento
        treeView.style.width = `${newWidth}px`;
        treeView.style.minWidth = `${newWidth}px`;
        treeView.style.maxWidth = `${newWidth}px`;

        // Mover o divisor para a nova posição
        divider.style.left = `${newWidth}px`;

        // Ajustar a área de conteúdo para evitar sobreposição
        if (fileContentArea) {
          fileContentArea.style.transition = 'none';
        }
      }

      // Prevenir seleção de texto e outros comportamentos padrão
      event.preventDefault();
    },

    stopResize() {
      this.isDragging = false;
      document.body.classList.remove('resizing');

      // Restaurar as transições da área de conteúdo
      const fileContentArea = document.querySelector('.file-content-area');
      if (fileContentArea) {
        fileContentArea.style.transition = '';
      }
    },

    getTypeLabel(item) {
      if (item.type === 'directory') return 'Pasta';
      if (item.type === 'drive') return 'Drive';
      return this.formatFileType(item);
    },

    showItemCounts(item) {
      return item.type === 'directory' || item.type === 'drive';
    },

    /**
     * Função genérica para atualizar a árvore após qualquer operação (criar/excluir/mover/renomear)
     * @param {Object} item O item afetado pela operação
     * @param {String} action A ação realizada: 'create', 'delete', 'update', 'move', 'rename'
     */
    updateTreeAfterOperation(item, action) {
      if (!item) {
        console.warn('Tentativa de atualizar árvore sem especificar o item');
        return;
      }

      console.log(`Atualizando árvore após ${action} de ${item.type}: ${item.name}`);

      // Primeiro, recarregar a lista de itens da pasta atual
      this.loadItems();

      // Em seguida, atualizar a árvore
      this.$nextTick(() => {
        const fileTree = this.$refs.fileTree;

        if (!fileTree) {
          console.warn('Referência ao componente FileTree não encontrada');
          return;
        }

        // Identificar o nó pai que precisa ser atualizado
        const parentId = item.parent_id;

        if (parentId) {
          console.log(`Atualizando árvore para parent_id: ${parentId}`);

          // Para renomeação, incluir informações específicas no evento
          const eventData = {
            updatedNodeId: parentId,
            action: action,
            itemId: item.resource_id,
            itemType: item.type
          };

          // Para renomeação, adicionar mais informações para otimizar a atualização
          if (action === 'rename') {
            Object.assign(eventData, {
              newName: item.name,
              newPath: item.path,
              oldName: item.oldName || ''
            });
          }

          // Atualizar o nó específico na árvore
          fileTree.refreshSubTree(parentId);

          // Emitir evento global para atualizar todos os nós afetados
          this.$root.$emit('tree-structure-updated', eventData);

          // Para operações que podem afetar ancestrais, emitir um evento adicional
          if ((action === 'create' || action === 'delete') &&
              (item.type === 'directory' || this.currentPath !== '')) {

            this.$root.$emit('refresh-tree-counters', {
              type: action,
              itemId: item.resource_id,
              parentId: parentId
            });
          }
        } else {
          console.log('Item no nível raiz, atualizando toda a árvore');
          fileTree.loadRootItems();
        }
      });
    },

    /**
     * Manipular o evento de exclusão de item
     * @param {Object} item Item que foi excluído
     */
    handleItemDeleted(item) {
      // Usar a função genérica de atualização
      this.updateTreeAfterOperation(item, 'delete');
    },

    /**
     * Manipular o evento de criação de novo diretório
     * @param {Object} newFolder Novo diretório criado
     */
    handleFolderCreated(newFolder) {
      // Usar a função genérica de atualização
      this.updateTreeAfterOperation(newFolder, 'create');
    },

    /**
     * Manipular o evento de upload de arquivo
     * @param {Object} newFile Novo arquivo carregado
     */
    handleFileUploaded(newFile) {
      // Usar a função genérica de atualização
      this.updateTreeAfterOperation(newFile, 'create');
    },

    /**
     * Exibe o modal para renomear um item
     * @param {Object} item O item a ser renomeado
     */
    showRenameItem(item) {
      this.renameItem = item;

      // Se for um arquivo, mostrar apenas o nome sem a extensão
      if (item.type === 'file') {
        const nameParts = item.name.split('.');
        // Se tiver extensão, separar o nome da extensão
        if (nameParts.length > 1) {
          // Caso o arquivo tenha múltiplos pontos, considerar apenas o último como extensão
          const extension = nameParts.pop();
          this.newItemName = nameParts.join('.');
        } else {
          // Se não tiver extensão, usar o nome completo
          this.newItemName = item.name;
        }
      } else {
        // Se for pasta ou drive, usar o nome completo
        this.newItemName = item.name;
      }

      this.showRenameModal = true;

      // Focar no campo de entrada após a exibição do modal
      this.$nextTick(() => {
        if (this.$refs.renameInput) {
          this.$refs.renameInput.focus();
          this.$refs.renameInput.select();
        }
      });
    },

    /**
     * Cancela a operação de renomeação
     */
    cancelRename() {
      this.showRenameModal = false;
      this.renameItem = null;
      this.newItemName = '';
    },

    /**
     * Confirma a renomeação do item
     */
    async confirmRename() {
      if (!this.newItemName || !this.renameItem) return;

      // Para arquivos, verificar se o nome (sem a extensão) mudou
      let originalName = this.renameItem.name;
      let newFullName = this.newItemName;

      if (this.renameItem.type === 'file') {
        // Extrair a extensão original
        const nameParts = originalName.split('.');
        if (nameParts.length > 1) {
          const extension = nameParts.pop();
          // Adicionar a extensão original ao novo nome
          newFullName = `${this.newItemName}.${extension}`;
        }
      }

      // Se o nome completo for igual, apenas fechar o modal
      if (newFullName === originalName) {
        this.cancelRename();
        return;
      }

      this.isRenaming = true;

      try {
        // Criar uma cópia do item com as informações necessárias para atualização futura
        const itemToRename = { ...this.renameItem };

        // Preparar os parâmetros para a API
        const params = {
          path: itemToRename.path,
          name: newFullName,         // Usar "name" como nome do parâmetro
          resource_id: itemToRename.resource_id,
          type: itemToRename.type
        };

        // Adicionar media_id se for um arquivo
        if (itemToRename.type === 'file' && itemToRename.media_id) {
          params.media_id = itemToRename.media_id;
        }

        // Chamar o serviço de renomeação com os parâmetros corretos
        const response = await this.fileExplorerService.rename(
          params.path,
          params.name,
          params.resource_id,
          params.type,
          params.media_id  // Adicionar media_id como parâmetro opcional
        );

        this.notifySuccess(this.$vs, `${itemToRename.type === 'directory' ? 'Pasta renomeada' : 'Arquivo renomeado'} com sucesso.`);

        console.log('Item renomeado com sucesso:', response);

        // Criar um objeto representando o item renomeado para atualização da árvore
        const renamedItem = {
          ...itemToRename,
          name: newFullName,
          path: itemToRename.path.replace(itemToRename.name, newFullName),
          // Se a API retornar novos IDs, usá-los aqui
          resource_id: response && response.resource_id ? response.resource_id : itemToRename.resource_id,
          parent_id: itemToRename.parent_id
        };

        // Emitir evento para o componente pai
        this.$emit('item-renamed', itemToRename, newFullName);

        // Atualizar a interface
        this.updateTreeAfterOperation(renamedItem, 'rename');

        // Recarregar a lista de itens atual
        this.loadItems();

        // Fechar o modal
        this.cancelRename();
      } catch (error) {
        console.error('Erro ao renomear item:', error);

      } finally {
        this.isRenaming = false;
      }
    },

    /**
     * Permite a navegação através do breadcrumb
     * @param {Number} index O índice do item no breadcrumb para onde navegaremos
     */
    navigateToBreadcrumb(index) {
      // Se clicar no último item, não fazer nada (já estamos nele)
      if (index === this.breadcrumbItems.length - 1) return;

      // Pegar o item do breadcrumb no índice clicado
      const item = this.breadcrumbItems[index];

      // Navegar para o item
      this.currentPath = item.path;
      this.currentResourceId = item.resource_id;

      // Atualizar o breadcrumb para mostrar apenas até o item clicado
      this.breadcrumbItems = this.breadcrumbItems.slice(0, index + 1);

      // Carregar os itens do diretório
      this.loadCurrentItems();
    },

    async showFilePreview(item) {
      try {
        // Obter a URL do preview do arquivo
        const previewUrl = await this.fileExplorerService.getPreviewUrl(item);

        // Se tiver URL de preview, mostrar o modal
        if (previewUrl) {
          this.previewFile = item;
          this.previewUrl = previewUrl;
          this.showPreviewModal = true;
        } else {
          // Se não tiver preview, emitir evento file-open
          this.$emit('file-open', item);
        }
      } catch (error) {
        console.error('Erro ao obter preview do arquivo:', error);
        // Em caso de erro, emitir evento file-open
        this.$emit('file-open', item);
      }
    },

    closePreview() {
      this.showPreviewModal = false;
      this.previewFile = null;
      this.previewUrl = null;
    },

    handlePreviewDownload() {
      if (this.previewFile) {
        this.$emit('download-file', this.previewFile);
      }
    },

    /**
     * Manipula navegação na treeview de compartilhados
     */
    handleSharedTreeNavigation(item) {
      this.isBrowsingShared = true;
      this.sharedCurrentPath = item.path;
      this.sharedCurrentResourceId = item.resource_id;
      // Simula busca dos filhos do item compartilhado
      // Aqui você pode adaptar para buscar do backend depois
      if (item.type === 'directory' || item.type === 'drive') {
        // Para o mock, filtra os filhos diretos
        this.sharedCurrentItems = this.sharedItems.filter(child => child.parent_id === item.resource_id);
      } else {
        this.sharedCurrentItems = [item];
      }
      // Limpa seleção de arquivos próprios
      this.selected = null;
    },
  }
}
</script>

<style lang="scss">
/* Estilos movidos para o arquivo @/assets/scss/FileExplorer.scss */
.shared-divider {
  border: none;
  border-top: 2px solid #e0e0e0;
  margin: 32px 0 24px 0;
}
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #444;
}
.file-item.shared-item, .file-row.shared-item {
  background: #f7fafd;
}
.tree-shared-divider {
  border: none;
  border-top: 2px solid #e0e0e0;
  margin: 18px 0 12px 0;
}
.tree-section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #444;
  padding-left: 8px;
}
</style>
